/**
 * RentalTide Service - transforms RT marketplace inventory into Sharetribe listing shape.
 *
 * Follows the same pattern as dynamodbService.js for consistency.
 */

import { types as sdkTypes } from './sdkLoader';
import { searchRentalTideListings as apiSearchRentalTideListings } from './api';

const { LatLng: SDKLatLng, Money } = sdkTypes;

/**
 * Transform a RentalTide inventory item into a Sharetribe-compatible listing object.
 */
export const transformRentalTideToSharetribeListing = rtItem => {
  try {
    // Geolocation
    let geolocation = null;
    if (rtItem.location?.latitude && rtItem.location?.longitude) {
      const lat = parseFloat(rtItem.location.latitude);
      const lng = parseFloat(rtItem.location.longitude);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        geolocation = new SDKLatLng(lat, lng);
      }
    }

    // Images → Sharetribe image format
    const images = (rtItem.images || []).map((url, index) => ({
      id: `rt-${rtItem.inventoryId}-img-${index}`,
      type: 'image',
      attributes: {
        variants: {
          'listing-card': { url, width: 400, height: 300 },
          'listing-card-2x': { url, width: 800, height: 600 },
        },
      },
    }));

    // Price → Sharetribe Money type (amount in cents)
    const priceValue = parseFloat(rtItem.startingPrice || 0);
    const priceInCents = Math.round(priceValue * 100);
    const currency = (rtItem.currency || 'CAD').toUpperCase();
    const price = new Money(priceInCents, currency);

    const result = {
      id: { uuid: `rt-${rtItem.inventoryId}`, type: 'listing' },
      type: 'listing',
      attributes: {
        title: rtItem.inventoryName || 'Rental',
        description: rtItem.description || '',
        price,
        geolocation,
        state: 'published',
        publicData: {
          listingType: 'rental',
          transactionProcessAlias: 'default-booking',
          unitType: rtItem.priceUnit === 'day' ? 'day' : 'hour',
          isRentalTideListing: true,
          isPartnerExperience: true,
          partneredWith: 'RentalTide',
          category: rtItem.category,
          capacity: rtItem.capacity,
          bookingUrl: rtItem.bookingUrl,
          priceUnit: rtItem.priceUnit,
          amenities: rtItem.amenities,
        },
      },
      images,
      author: {
        id: { uuid: `rt-${rtItem.customerId}`, type: 'user' },
        type: 'user',
        attributes: {
          profile: {
            displayName: rtItem.location?.name || 'RentalTide',
            abbreviatedName: 'RT',
          },
        },
      },
      relationships: {
        author: {
          data: {
            id: { uuid: `rt-${rtItem.customerId}`, type: 'user' },
            type: 'user',
          },
        },
        images: {
          data: images.map(img => ({ id: img.id, type: 'image' })),
        },
      },
    };

    return result;
  } catch (error) {
    console.error('Error transforming RentalTide item:', error);
    throw error;
  }
};

/**
 * Fetch RentalTide listings, transform them, and return in the standard { data, meta } shape.
 */
export const fetchRentalTideListings = async (searchParams = {}) => {
  try {
    const response = await apiSearchRentalTideListings(searchParams);

    if (!response || !response.listings || response.listings.length === 0) {
      return {
        data: [],
        meta: { totalItems: 0, totalPages: 0, page: 1, perPage: 0 },
      };
    }

    const transformedListings = [];
    for (let i = 0; i < response.listings.length; i++) {
      try {
        const item = response.listings[i];
        // Skip demo/test listings (customerId 1)
        if (String(item.customerId) === '1') continue;
        const transformed = transformRentalTideToSharetribeListing(item);
        transformedListings.push(transformed);
      } catch (transformError) {
        console.error(`Error transforming RentalTide item ${i + 1}:`, transformError);
      }
    }

    return {
      data: transformedListings,
      meta: {
        totalItems: response.total || transformedListings.length,
        totalPages: Math.ceil((response.total || transformedListings.length) / (searchParams.perPage || 100)),
        page: searchParams.page || 1,
        perPage: searchParams.perPage || transformedListings.length,
      },
    };
  } catch (error) {
    console.error('Error fetching RentalTide listings:', error);
    return {
      data: [],
      meta: { totalItems: 0, totalPages: 0, page: 1, perPage: 0 },
    };
  }
};
